/**
 * The one place the surface's capability seams are assembled.
 *
 * `runtime.ts` refuses every tool call until `installCapabilities` has been
 * handed a `{ read, write }` pair, and that refusal is retryable rather than
 * fatal precisely so this module can be absent during boot. This is the module
 * that ends that state. It composes the lanes, and it owns nothing else: every
 * seam body lives in the lane that is allowed to reach it.
 *
 * COMPOSITION IS BY NAME, NOT BY SPREAD, AND THAT IS THE SAFETY PROPERTY.
 *
 * A lane publishes one `Partial<ReadDeps & WriteDeps>`, which is convenient for
 * the lane and dangerous here: spreading such a value into the read object
 * would put `setCalendarDate` on the thing a `resolve` step holds, and the
 * contract's whole claim is that a read step has no dependency path to a write.
 * So each side picks the seams it is allowed to carry, by name. The two pick
 * lists are no longer written out by hand. Each one derives from the optional
 * keys of its own interface in `contract.ts`, so a seam declared there and left
 * out of the list is a compile error instead of a seam that is quietly never
 * installed.
 *
 * That silence was not hypothetical. `feedSeams.ts` and `tableSeams.ts` landed
 * while this file still carried TODO comments claiming they did not exist, and
 * six planner tools answered "unavailable on this page" on production for as
 * long as those lanes stayed out of `LANES`.
 */
import { __policy, brandBibleWriter } from './brandBibleWriter';
import { calendarSeams } from './calendarSeams';
import { feedSeams } from './feedSeams';
import { tableSeams } from './tableSeams';
import { editorSeams } from './editorSeams';
import { dirtySectionsFor } from './focus';
import type { ReadDeps, TenantId, WriteDeps } from './contract';
import type { Capabilities } from './runtime';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import type { BbTable } from '@/features/brand-bible-wizard/types';

/** What a lane publishes. Every member optional; the lane supplies its own. */
type Lane = Partial<ReadDeps & WriteDeps>;

// ---------------------------------------------------------------------------
// The Brand Bible read side
// ---------------------------------------------------------------------------

/**
 * The Brand Bible tables that hold exactly one row per tenant.
 *
 * Restated here rather than imported, and that cost is paid on purpose. The
 * authoritative list is `isSingletonTable` (`brandBibleWizardStore.ts:128`),
 * which is module private, and the identical `SINGLETON_TABLES`
 * (`brandBibleWriter.ts:208`), which `__policy` does not export. Both name
 * these same three. Nothing may be added here that is not a singleton there
 * first: a table listed here resolves with NO id, so listing a list-type table
 * would silently hand back one arbitrary row out of many.
 */
const SINGLETON_TABLES: readonly string[] = ['identity', 'voice_profile', 'visual_identity'];

/**
 * `ReadDeps.currentRow`. Settles the wizard store on `tenant` and returns the
 * identified row, or null.
 *
 * Three deliberate refusals to guess.
 *
 *  - A table that is not a Brand Bible v2 entry table returns null WITHOUT
 *    touching the store. `WRITABLE_TABLES` is that list, exported by the writer
 *    for callers that must not hold the store handle. Without this gate every
 *    planner write would drag a full brand bible load through `awaitLoaded`.
 *  - No id on a LIST table returns null, still without touching the store.
 *    There is no "the row" to mean, and choosing one would be the guess.
 *  - A load that does not settle on `tenant` returns null rather than reading
 *    whatever rows happen to be in the store. `awaitLoaded` short-circuits when
 *    `loadedTenantId` already matches (`brandBibleWizardStore.ts:587`), so the
 *    post-check is what makes a stale tenant visible.
 *
 * NO ID ON A SINGLETON RESOLVES THE ONE LIVE ROW, AND THAT IS NOT A GUESS.
 * A singleton has exactly one row per tenant and no agent can know its id, so
 * "no id" there names the row unambiguously. It is picked by the SAME rule the
 * panels use (`useSingleton.ts:13`), which is what makes the agent write to the
 * row the operator has open rather than a sibling the UI ignores;
 * `deleted_at` is part of that rule because the writer refuses a deleted row
 * (`brandBibleWriter.ts:588`) and returning one would turn a legitimate write
 * into an unexplainable refusal.
 *
 * A tenant with no such row yet still returns null, which is the legitimate
 * create, and the create path stays intact BECAUSE the two callers read through
 * this one seam. `brandBibleWriter` refuses a create that carries a base
 * version (`brandBibleWriter.ts:581`), so the command's `resolve` and the
 * runtime's mint must agree. They do: a `resolve` that found no row emits
 * `rowId: null`, the mint maps that back to `undefined` (`runtime.ts:1096`),
 * re-reads the same empty store and mints `baseVersion` null. A `resolve` that
 * DID find the row emits its id, and the mint takes the id-present path below
 * to that same row and its real version, so a second write against an existing
 * profile is a genuine read before write.
 */
async function currentRow(
  table: string,
  tenant: TenantId,
  id?: string,
): Promise<Readonly<Record<string, unknown>> | null> {
  if (__policy.WRITABLE_TABLES.indexOf(table) < 0) return null;

  const byId = typeof id === 'string' && id.length > 0;
  if (!byId && SINGLETON_TABLES.indexOf(table) < 0) return null;

  const store = (): ReturnType<typeof useBrandBibleWizardStore.getState> =>
    useBrandBibleWizardStore.getState();

  let loaded = false;
  try {
    loaded = await store().awaitLoaded(tenant);
  } catch {
    // The runtime treats a missing base version as a weakened write, not an
    // invalid one, and logs it. A thrown load is the same fact, so it is
    // reported the same way rather than as an exception the pipeline must
    // classify.
    return null;
  }
  if (!loaded) return null;
  if (store().loadedTenantId !== tenant) return null;

  const rows = store().entries[table as BbTable];
  if (!rows) return null;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row) continue;
    const match = byId ? row.id === id : row.location_scope === 'all' && row.deleted_at === null;
    // Through `unknown` because `BbEntry` is an intersection of interfaces and
    // so carries no implicit index signature. It widens nothing: the runtime
    // reads this row by NAME for a base version and never spreads it.
    if (match) return row as unknown as Readonly<Record<string, unknown>>;
  }
  return null;
}

/**
 * `ReadDeps.brandBibleLoaded`. Whether the wizard store has settled on
 * `tenant`, which is the one fact `currentRow` cannot publish.
 *
 * A null from `currentRow` is two facts wearing one value, a table holding no
 * row and a store that never settled, and the caller deciding between creating
 * a row and refusing needs them apart. Before this seam that caller inferred
 * settlement from a sibling singleton resolving, which said nothing about a
 * tenant whose Brand Bible is loaded and entirely empty.
 *
 * `awaitLoaded` (`brandBibleWizardStore.ts:587`) is already the answer. It
 * short-circuits true when `loadedTenantId` matches, waits on the in-flight
 * load otherwise, and returns false on its 30 second default timeout, on a
 * failed load, and on a load that settled on some other tenant. A throw is
 * that same "not settled" fact arriving by another route, so it is reported as
 * false rather than raised at a caller whose only answer would be to refuse.
 */
async function brandBibleLoaded(tenant: TenantId): Promise<boolean> {
  try {
    return await useBrandBibleWizardStore.getState().awaitLoaded(tenant);
  } catch {
    return false;
  }
}

/**
 * The Brand Bible lane. It supplies the two required reads, the settlement
 * read that only this lane can answer, and the one required write.
 *
 * `commit` is bound rather than referenced. The writer's `commit` is a closure
 * over `attempt` and never reads `this` today, and binding keeps that true for
 * the next author instead of leaving a detached method that breaks silently.
 */
const brandBibleLane = {
  currentRow,
  brandBibleLoaded,
  dirtySections: dirtySectionsFor,
  commit: brandBibleWriter.commit.bind(brandBibleWriter),
} satisfies Lane;

// ---------------------------------------------------------------------------
// Lane order
// ---------------------------------------------------------------------------

/**
 * The lanes, in install order.
 *
 * LATER ENTRIES WIN ON DUPLICATE KEYS. `collectReadSeams` and
 * `collectWriteSeams` walk this array front to back and overwrite, so a lane
 * listed further down replaces an earlier lane's seam of the same name. A lane
 * that simply omits a seam never blanks one an earlier lane supplied.
 *
 * The Brand Bible lane's own seams are NOT merged from here, neither the two
 * required reads, nor `brandBibleLoaded`, nor the required `commit`. They are
 * named directly on `brandBibleLane` below, so their presence is a type check
 * rather than a runtime hope, and a later lane cannot quietly take over the
 * write path to the Brand Bible or the settlement answer that write path's
 * create-or-refuse decision rests on.
 */
const LANES: readonly Lane[] = [brandBibleLane, calendarSeams, feedSeams, tableSeams, editorSeams];

/**
 * The optional members of an interface, as a union of their names.
 *
 * Both pick lists below are checked against one of these unions, which is the
 * whole point. A required member stays out because a lane cannot make it
 * optional, and an optional member cannot stay out, because the check demands
 * every name.
 */
type OptionalSeamName<D> = {
  [K in keyof D]-?: Record<never, never> extends Pick<D, K> ? K : never;
}[keyof D];

/**
 * The read seams a lane may supply.
 *
 * `brandBibleLoaded` is optional on `ReadDeps` and is still excluded here. It
 * is named directly on `brandBibleLane` for the reason LANES gives, and
 * merging it would let a later lane answer the settlement question the Brand
 * Bible create-or-refuse decision rests on.
 */
type MergedReadSeam = Exclude<OptionalSeamName<ReadDeps>, 'brandBibleLoaded'>;

/** The write seams a lane may supply. `commit` is required, so it is not one. */
type MergedWriteSeam = OptionalSeamName<WriteDeps>;

/**
 * The pick lists, written as objects so `satisfies` can hold them to their
 * side of the contract. A seam declared in `contract.ts` and missing from one
 * of these is a missing required property, and a name belonging to the other
 * side is an excess one, which is what keeps a write seam off the value a
 * `resolve` step holds without anyone rechecking that by eye.
 */
const READ_SEAM_SET = {
  findArticles: true,
  findKeywordCandidates: true,
  editorContext: true,
} satisfies Record<MergedReadSeam, true>;

const WRITE_SEAM_SET = {
  setCalendarDate: true,
  scheduleWpPublish: true,
  createArticle: true,
  publishArticle: true,
  regenerate: true,
  approveKeyword: true,
  editorFormat: true,
  editorSaveDraft: true,
  editorSetCanonical: true,
  editorSetJsonldDescription: true,
  editorSetOgImage: true,
  editorPublishCms: true,
} satisfies Record<MergedWriteSeam, true>;

/** Exported so a test can walk the same names the merge walks. */
export const READ_SEAM_NAMES = Object.keys(READ_SEAM_SET) as readonly MergedReadSeam[];
export const WRITE_SEAM_NAMES = Object.keys(WRITE_SEAM_SET) as readonly MergedWriteSeam[];

/**
 * Takes the named seams out of each lane, front to back, so a later lane wins
 * a duplicate name and an omission blanks nothing.
 */
function collectSeams<Name extends keyof Lane>(
  lanes: readonly Lane[],
  names: readonly Name[],
): Partial<Pick<Lane, Name>> {
  const merged: Partial<Pick<Lane, Name>> = {};
  for (let i = 0; i < lanes.length; i += 1) {
    const lane = lanes[i];
    if (!lane) continue;
    for (let j = 0; j < names.length; j += 1) {
      const name = names[j];
      const seam = lane[name];
      if (seam) merged[name] = seam;
    }
  }
  return merged;
}

/** Every optional seam on the READ side. */
function collectReadSeams(lanes: readonly Lane[]): Partial<Pick<ReadDeps, MergedReadSeam>> {
  return collectSeams(lanes, READ_SEAM_NAMES);
}

/** Every optional seam on the WRITE side. `commit` is required and excluded. */
function collectWriteSeams(lanes: readonly Lane[]): Partial<Pick<WriteDeps, MergedWriteSeam>> {
  return collectSeams(lanes, WRITE_SEAM_NAMES);
}

/**
 * Builds the pair `runtime.installCapabilities` takes.
 *
 * Pure and synchronous. Every seam body reads its store or service at CALL
 * time, so this may run at module scope before React mounts without capturing
 * a workspace, a tenant, or a store snapshot that would later be wrong.
 *
 * A seam no lane supplied stays `undefined`, which is the contract's own
 * signal: the command that needs it discovers it was handed a capability that
 * cannot do the job and returns a refusal saying so.
 */
export function buildCapabilities(): Capabilities {
  const read: ReadDeps = {
    currentRow: brandBibleLane.currentRow,
    // Optional on `ReadDeps`, and still named rather than merged, for the
    // reason given at LANES above.
    brandBibleLoaded: brandBibleLane.brandBibleLoaded,
    dirtySections: brandBibleLane.dirtySections,
    ...collectReadSeams(LANES),
  };

  const write: WriteDeps = {
    commit: brandBibleLane.commit,
    ...collectWriteSeams(LANES),
  };

  return { read, write };
}
